import { mergeStyleSets, IStyle } from '@fluentui/react/lib/Styling';

const recipientBadgeBase: IStyle = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '18px',
};

export const styles = mergeStyleSets({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 48px)',
    backgroundColor: '#faf9f8',
    fontFamily: '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
  } as IStyle,

  pageHeader: {
    padding: '20px 24px 12px',
    borderBottom: '1px solid #edebe9',
    backgroundColor: '#ffffff',
  } as IStyle,

  pageTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#323130',
    margin: 0,
  } as IStyle,

  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    margin: '4px 0 0',
  } as IStyle,

  contentArea: {
    flex: 1,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  } as IStyle,

  listContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    padding: '16px 24px',
  } as IStyle,

  filterBar: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: '12px',
    marginBottom: '16px',
    padding: '12px 16px',
    backgroundColor: '#ffffff',
    borderRadius: '4px',
    border: '1px solid #edebe9',
  } as IStyle,

  filterLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#323130',
    whiteSpace: 'nowrap',
  } as IStyle,

  searchBox: {
    maxWidth: '260px',
  } as IStyle,

  tableWrapper: {
    flex: 1,
    minHeight: 0,
    backgroundColor: '#ffffff',
    borderRadius: '4px',
    border: '1px solid #edebe9',
    overflow: 'auto',
  } as IStyle,

  badgeManager: [
    recipientBadgeBase,
    {
      backgroundColor: '#f3f2f1',
      color: '#605e5c',
    },
  ] as IStyle,

  badgeEmployee: [
    recipientBadgeBase,
    {
      backgroundColor: '#deecf9',
      color: '#0078d4',
    },
  ] as IStyle,

  badgeHRC: [
    recipientBadgeBase,
    {
      backgroundColor: '#dff6dd',
      color: '#107c10',
    },
  ] as IStyle,

  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 999,
  } as IStyle,

  editPanelContainer: {
    position: 'fixed',
    top: '48px',
    right: 0,
    width: 'min(680px, 100vw)',
    maxWidth: '100vw',
    height: 'calc(100vh - 48px)',
    backgroundColor: '#ffffff',
    boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    selectors: {
      '.ms-Button': { marginRight: '0 !important' },
    },
  } as IStyle,

  editPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px 16px',
    borderBottom: '1px solid #edebe9',
  } as IStyle,

  editPanelHeaderTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#323130',
    margin: 0,
  } as IStyle,

  editPanelHeaderCode: {
    fontSize: '12px',
    color: '#a19f9d',
    fontFamily: 'Consolas, "Courier New", monospace',
    marginTop: '4px',
  } as IStyle,

  editPanelBody: {
    flex: 1,
    overflow: 'auto',
    padding: '20px 24px',
  } as IStyle,

  readOnlyGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: '#faf9f8',
    borderRadius: '4px',
    border: '1px solid #edebe9',
  } as IStyle,

  readOnlyField: {
    display: 'flex',
    flexDirection: 'column',
  } as IStyle,

  readOnlyLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#605e5c',
    marginBottom: '4px',
    textTransform: 'uppercase',
  } as IStyle,

  readOnlyValue: {
    fontSize: '14px',
    color: '#323130',
  } as IStyle,

  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#323130',
    marginTop: '20px',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #edebe9',
  } as IStyle,

  editorToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px',
    borderBottom: '1px solid #edebe9',
    backgroundColor: '#faf9f8',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    border: '1px solid #8a8886',
    borderBottomColor: '#edebe9',
    flexWrap: 'wrap',
  } as IStyle,

  editorToolbarButton: {
    minWidth: '32px',
    height: '32px',
    padding: '4px 8px',
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    borderRadius: '2px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#323130',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    selectors: {
      ':hover': {
        backgroundColor: '#f3f2f1',
        border: '1px solid #c8c6c4',
      },
    },
  } as IStyle,

  editorToolbarSeparator: {
    width: '1px',
    height: '20px',
    backgroundColor: '#edebe9',
    margin: '0 4px',
  } as IStyle,

  richTextEditorFrame: {
    display: 'block',
    width: '100%',
    minHeight: '320px',
    height: '320px',
    border: '1px solid #8a8886',
    borderTop: 'none',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    resize: 'vertical',
    selectors: {
      ':focus-within': {
        borderColor: '#0078d4',
      },
    },
  } as IStyle,

  variablesPanel: {
    marginTop: '16px',
    padding: '12px 16px',
    backgroundColor: '#faf9f8',
    borderRadius: '4px',
    border: '1px solid #edebe9',
  } as IStyle,

  variablesPanelTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#605e5c',
    marginBottom: '8px',
  } as IStyle,

  variableTagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  } as IStyle,

  variableTag: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    backgroundColor: '#deecf9',
    color: '#0078d4',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    border: '1px solid transparent',
    fontFamily: 'Consolas, "Courier New", monospace',
    selectors: {
      ':hover': {
        backgroundColor: '#c7e0f4',
        border: '1px solid #0078d4',
      },
    },
  } as IStyle,

  editPanelFooter: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #edebe9',
    backgroundColor: '#faf9f8',
  } as IStyle,

  footerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    selectors: {
      '.ms-Button': { flexShrink: 0, whiteSpace: 'nowrap', minWidth: '110px' },
    },
  } as IStyle,

  lastSaved: {
    marginLeft: 'auto',
    fontSize: '12px',
    color: '#605e5c',
  } as IStyle,

  previewModal: {
    padding: '24px',
  } as IStyle,

  previewField: {
    display: 'flex',
    marginBottom: '8px',
    fontSize: '14px',
  } as IStyle,

  previewFieldLabel: {
    fontWeight: 600,
    color: '#605e5c',
    minWidth: '60px',
    marginRight: '8px',
  } as IStyle,

  previewFieldValue: {
    color: '#323130',
  } as IStyle,

  previewBody: {
    marginTop: '16px',
    padding: 0,
    border: '1px solid #edebe9',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    width: '100%',
    height: '360px',
    display: 'block',
  } as IStyle,

  accessDenied: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    gap: '16px',
  } as IStyle,

  accessDeniedTitle: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#323130',
  } as IStyle,

  accessDeniedText: {
    fontSize: '14px',
    color: '#605e5c',
  } as IStyle,

  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '300px',
  } as IStyle,

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#605e5c',
    fontSize: '14px',
  } as IStyle,

  fieldGroup: {
    marginBottom: '16px',
  } as IStyle,
});
